import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, RotateCcw } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { api, errText } from "../../lib/api";

const RANGES = [[7, "7 days"], [30, "30 days"], [0, "All time"]];
const COLORS = { views: "#94A3B8", clicks: "#F5551E", signups: "#12234A" };
const short = (d) => new Date(`${d}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" });

const Tile = ({ id, label, value, sub }) => (
  <div className="rounded-xl bg-slate-50 px-4 py-3" data-testid={`popup-stats-${id}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="font-display text-2xl font-bold text-[#12234A] tabular-nums">{value.toLocaleString()}</p>
    {sub && <p className="text-xs text-slate-500">{sub}</p>}
  </div>
);

export function PopupStats({ page, signup }) {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/site/popup/stats", { params: { page, days } }).then((r) => setD(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, [page, days]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = async () => {
    if (!window.confirm("Reset all stats for this popup? This can't be undone.")) return;
    try { await api.delete("/admin/site/popup/stats", { params: { page } }); toast.success("Stats reset"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!d) return null;
  const t = d.totals;
  return (
    <div className="rounded-xl border border-slate-200 p-4 mb-5" data-testid="popup-stats">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-sm font-semibold text-[#12234A] flex items-center gap-2"><BarChart3 size={15} className="text-[#F5551E]" /> Stats</p>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs" data-testid="popup-stats-range">
            {RANGES.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setDays(v)} data-testid={`popup-stats-range-${v}`}
                className={`px-2.5 py-1 font-semibold transition-colors ${days === v ? "bg-[#12234A] text-white" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <button type="button" onClick={reset} className="text-xs font-semibold text-slate-400 hover:text-red-600 flex items-center gap-1" data-testid="popup-stats-reset"><RotateCcw size={12} /> Reset</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Tile id="views" label="Views" value={t.views} sub="times it opened" />
        <Tile id="clicks" label="Button taps" value={t.clicks} sub={`${t.click_rate}% of views`} />
        <Tile id="signups" label="Sign-ups" value={t.signups} sub={signup || t.signups ? `${t.signup_rate}% of views` : "sign-up form is off"} />
      </div>
      {t.views + t.clicks + t.signups > 0 ? (
        <div className="h-48 mt-4" data-testid="popup-stats-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.daily} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="day" tickFormatter={short} tick={{ fontSize: 10, fill: "#94A3B8" }} interval="preserveStartEnd" minTickGap={16} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} />
              <Tooltip labelFormatter={short} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
              <Bar dataKey="views" name="Views" fill={COLORS.views} radius={[3, 3, 0, 0]} />
              <Bar dataKey="clicks" name="Button taps" fill={COLORS.clicks} radius={[3, 3, 0, 0]} />
              <Bar dataKey="signups" name="Sign-ups" fill={COLORS.signups} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <p className="text-xs text-slate-400 text-center py-6" data-testid="popup-stats-empty">No activity in this period yet. Admin test popups aren't counted.</p>}
    </div>
  );
}
